import { useEffect, useMemo, useState } from "react";
import { HeartHandshake, BookOpen, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  getSponsorTotals,
  createSponsorOrder,
  verifySponsorPayment,
} from "@/api/client";
import { SPONSOR_QUICK_AMOUNTS } from "@/config/journeys";

const LANG_OPTIONS = [
  { key: "en", label: "English", native: "English" },
  { key: "hi", label: "Hindi", native: "हिन्दी" },
  { key: "mr", label: "Marathi", native: "मराठी" },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// "Sponsor a Srimad Bhagavatam" — lets a devotee fund a set for someone who
// can't afford it. Language-specific price + live progress toward one book,
// count of books already sponsored, quick amounts and Razorpay checkout.
export const SponsorBhagavatam = ({ sponsor, slug, lang = "en" }) => {
  const prices = sponsor?.prices || { en: 8100, hi: 6400, mr: 5900 };
  const targetSlug = slug || "home";
  const hi = lang === "hi";

  const [bookLang, setBookLang] = useState("en");
  const [raised, setRaised] = useState({ en: 0, hi: 0, mr: 0 });
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [amount, setAmount] = useState(String(SPONSOR_QUICK_AMOUNTS[1]));
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    getSponsorTotals(targetSlug)
      .then((d) => {
        setRaised(d.raised || { en: 0, hi: 0, mr: 0 });
        setPaymentsEnabled(!!d.payments_enabled);
      })
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, [targetSlug]);

  const stats = useMemo(() => {
    const price = Math.max(1, Number(prices[bookLang] || 0));
    const total = Number(raised[bookLang] || 0);
    const sponsored = Math.floor(total / price);
    const towardNext = total % price;
    const balance = price - towardNext;
    const pct = Math.min(100, Math.round((towardNext / price) * 100));
    return { price, total, sponsored, towardNext, balance, pct };
  }, [prices, raised, bookLang]);

  const handleSponsor = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt < 1) {
      toast.error(hi ? "कृपया राशि दर्ज करें" : "Please enter an amount");
      return;
    }
    setBusy(true);
    try {
      const order = await createSponsorOrder(targetSlug, {
        lang: bookLang,
        amount: amt,
        name,
        phone,
        email,
      });
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load the payment window");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Take Krishna Home",
        description: `Sponsor a ${
          LANG_OPTIONS.find((l) => l.key === bookLang)?.label
        } Srimad Bhagavatam`,
        prefill: { name, email, contact: phone },
        theme: { color: "#f59e0b" },
        handler: async (resp) => {
          try {
            const res = await verifySponsorPayment(targetSlug, {
              donation_id: order.donation_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setRaised(res.raised);
            toast.success(
              hi
                ? "हरे कृष्ण! आपका योगदान प्राप्त हुआ 🙏"
                : "Hare Krishna! Your sponsorship is confirmed 🙏"
            );
          } catch {
            toast.error("We couldn't confirm the payment. Please contact us.");
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", () => {
        toast.error(hi ? "भुगतान विफल रहा" : "Payment failed. Please try again.");
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Could not start the payment");
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-amber-300/20 bg-[#080b16]/70 px-4 py-3 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none transition-colors focus:border-amber-300/60";

  return (
    <div
      data-testid="sponsor-bhagavatam"
      className="mx-auto max-w-3xl px-5"
    >
      <div className="overflow-hidden rounded-3xl border border-amber-300/25 bg-gradient-to-b from-[#1a2140]/80 to-[#0a0f24] shadow-[0_0_60px_-20px_rgba(245,180,60,0.5)]">
        <div className="border-b border-amber-300/10 bg-amber-400/[0.04] px-6 py-7 text-center sm:px-10">
          <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-amber-400/15 text-amber-300">
            <HeartHandshake className="h-6 w-6" />
          </span>
          <h3 className="font-display text-3xl leading-tight text-amber-200 sm:text-4xl">
            {hi ? "एक श्रीमद् भागवतम् प्रायोजित करें" : "Sponsor a Srimad Bhagavatam"}
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm text-amber-50/60 md:text-base">
            {hi
              ? "उन भक्तों के लिए योगदान दें जो स्वयं भागवतम् नहीं ले सकते। आपकी सेवा किसी के घर कृष्ण को पहुँचाएगी।"
              : "Contribute for devotees who can't afford a set. Your seva brings Krishna's words into another home."}
          </p>
        </div>

        <div className="space-y-6 px-6 py-7 sm:px-10">
          {/* Language selector */}
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-wider text-amber-100/50">
              {hi ? "भाषा चुनें" : "Choose the language to sponsor"}
            </label>
            <div className="grid grid-cols-3 gap-2" data-testid="sponsor-lang-group">
              {LANG_OPTIONS.map((l) => (
                <button
                  key={l.key}
                  type="button"
                  data-testid={`sponsor-lang-${l.key}`}
                  onClick={() => setBookLang(l.key)}
                  className={`rounded-xl border px-3 py-3 text-center transition-all ${
                    bookLang === l.key
                      ? "border-amber-300 bg-amber-400/15 text-amber-100"
                      : "border-amber-300/15 bg-white/[0.02] text-amber-100/70 hover:border-amber-300/40"
                  }`}
                >
                  <span className="block font-display text-lg">{l.native}</span>
                  <span className="mt-1 block text-xs text-amber-200/80">
                    {inr(prices[l.key])}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Progress toward one book */}
          <div className="rounded-2xl border border-amber-300/12 bg-[#080b16]/50 p-5">
            <div className="mb-2 flex items-end justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-amber-100/45">
                  {hi ? "अगली प्रति के लिए" : "Toward the next copy"}
                </p>
                <p className="mt-1 font-display text-2xl text-amber-200">
                  {inr(stats.towardNext)}{" "}
                  <span className="text-sm text-amber-100/40">/ {inr(stats.price)}</span>
                </p>
              </div>
              <div className="text-right">
                <p
                  data-testid="sponsor-count"
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-3 py-1 text-sm font-semibold text-amber-200"
                >
                  <BookOpen className="h-4 w-4" />
                  {stats.sponsored} {hi ? "प्रायोजित" : "sponsored"}
                </p>
              </div>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-white/8">
              <div
                data-testid="sponsor-progress-bar"
                className="h-full min-w-[3px] rounded-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-500"
                style={{ width: `${stats.pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-amber-100/45">
              {hi
                ? `एक पूरी प्रति के लिए ${inr(stats.balance)} शेष`
                : `${inr(stats.balance)} left to complete one full copy`}
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="mb-1.5 block text-xs uppercase tracking-wider text-amber-100/50">
              {hi ? "योगदान राशि" : "Your contribution"}
            </label>
            <div className="flex flex-wrap gap-2">
              {SPONSOR_QUICK_AMOUNTS.map((q) => (
                <button
                  key={q}
                  type="button"
                  data-testid={`sponsor-quick-${q}`}
                  onClick={() => setAmount(String(q))}
                  className={`rounded-full border px-5 py-2 text-sm font-semibold transition-all ${
                    String(q) === amount
                      ? "border-amber-300 bg-amber-400 text-[#0a0f24]"
                      : "border-amber-300/25 text-amber-200 hover:border-amber-300/60 hover:bg-amber-400/10"
                  }`}
                >
                  {inr(q)}
                </button>
              ))}
              <button
                type="button"
                data-testid="sponsor-quick-full"
                onClick={() => setAmount(String(stats.balance))}
                className="rounded-full border border-amber-300/25 px-5 py-2 text-sm font-semibold text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10"
              >
                {hi ? "पूरी प्रति" : "Full copy"} ({inr(stats.balance)})
              </button>
            </div>
            <div className="mt-3 flex items-center rounded-xl border border-amber-300/20 bg-[#080b16]/70 px-4 focus-within:border-amber-300/60">
              <span className="text-lg text-amber-100/50">₹</span>
              <input
                data-testid="sponsor-amount-input"
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-transparent px-2 py-3 text-lg text-amber-50 outline-none"
                placeholder="500"
              />
            </div>
          </div>

          {/* Donor details */}
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              data-testid="sponsor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={hi ? "आपका नाम" : "Your name"}
              className={inputCls}
            />
            <input
              data-testid="sponsor-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={hi ? "फ़ोन" : "Phone"}
              className={inputCls}
            />
            <input
              data-testid="sponsor-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={hi ? "ईमेल" : "Email"}
              className={inputCls}
            />
          </div>

          <button
            data-testid="sponsor-pay-btn"
            onClick={handleSponsor}
            disabled={busy || !paymentsEnabled}
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-4 text-base font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <HeartHandshake className="h-5 w-5" />
            )}
            {paymentsEnabled
              ? hi
                ? `${inr(parseInt(amount, 10) || 0)} का योगदान दें`
                : `Sponsor ${inr(parseInt(amount, 10) || 0)}`
              : hi
              ? "दान जल्द ही शुरू होगा"
              : "Donations opening soon"}
          </button>

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-amber-100/40">
            <Check className="h-3.5 w-3.5 text-amber-300/70" />
            {hi
              ? "UPI, कार्ड और नेट-बैंकिंग द्वारा सुरक्षित भुगतान"
              : "Secure payment via UPI, cards & net-banking"}
          </p>
        </div>
      </div>
    </div>
  );
};
